<script setup lang="ts">
import { Cart, Item } from '@/@types/cart'
import Image from '@/assets/show/image.webp'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Counter, CounterDecrement, CounterIncrement, CounterValue } from '@/components/ui/counter'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Title } from '@/components/ui/title'
import { calculateTotalPrice, currentPriceFormat, formatPrice, shouldShowPromotion } from '@/helpers/current-price'
import { formatDateFull } from '@/helpers/format-date'
import { Link, router } from '@inertiajs/vue3'
import { useDebounceFn } from '@vueuse/core'
import { inject, toRefs } from 'vue'
import CouponSection from '../CouponSection.vue'
import RemoveCartItem from './RemoveCartItem.vue'

const props = defineProps<{ cart: Cart }>()

const { sessionCart } = inject<{
    sessionCart?: string
}>('stepControl', {
    sessionCart: '',
})

const { cart } = toRefs(props)

const updateItemQuantity = (item: Item) => {
    router.patch(
        sessionCart ? '/session_cart/update_item' : '/cart/update_item',
        {
            cart: {
                ticket_id: item.ticket_id,
                quantity: item.quantity,
            },
        },
        {
            only: ['cart', 'flash', 'coupon'],
            preserveScroll: true,
        },
    )
}

const scheduleUpdate = useDebounceFn((item: Item) => {
    updateItemQuantity(item)
}, 500)

const increment = (item: Item) => {
    item.quantity++
    scheduleUpdate(item)
}

const decrement = (item: Item) => {
    item.quantity--

    if (item.quantity < 1) item.quantity = 1

    scheduleUpdate(item)
}

function handleCounterInput(item: Item) {
    if (!item.quantity) item.quantity = 1

    if (item.quantity > 10000) item.quantity = 10000

    scheduleUpdate(item)
}
</script>

<template>
    <Title class="!text-2xl !font-semibold !text-gray-800">{{ $t('Tickets') }}</Title>

    <div class="font-inter flex flex-col gap-10">
        <Card>
            <Table class="w-max overflow-x-auto sm:w-full">
                <TableHeader>
                    <TableRow>
                        <TableHead class="w-7/12 py-6 pl-6 text-base font-semibold text-slate-800">
                            {{ $t('Tickets') }}
                        </TableHead>
                        <TableHead class="w-3/12 text-center text-base font-semibold text-slate-800">
                            {{ $t('Quantity') }}
                        </TableHead>
                        <TableHead class="text-base font-semibold text-slate-800">
                            {{ $t('Total') }}
                        </TableHead>
                        <TableHead> </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow v-for="item in cart.items" :key="item.id" class="border-dashed">
                        <TableCell class="flex gap-4 p-4">
                            <img :src="Image" alt="Ingresso" class="h-17 w-16 rounded-md" />
                            <div class="flex flex-col font-sans">
                                <p class="text-lg font-semibold text-slate-800">
                                    {{ $t(item.ticket.category) }}
                                </p>
                                <p class="text-sm font-normal">
                                    {{ formatDateFull(item.workshop.start || `2025-10-29`, $i18n.locale) }}
                                </p>
                                <p class="text-base font-normal">
                                    <small class="text-sm" v-if="shouldShowPromotion(item.ticket)">
                                        <span class="line-through">
                                            {{ formatPrice(Number((shouldShowPromotion(item.ticket) || 0) / 100)) }}
                                        </span>
                                    </small>
                                    {{ currentPriceFormat(item.ticket) }}
                                </p>
                            </div>
                        </TableCell>
                        <TableCell>
                            <Counter :min="1" :max="1500" :counter="item.quantity" class="justify-center">
                                <CounterDecrement @decrement="decrement(item)" />
                                <CounterValue class="" @input="handleCounterInput(item)" v-model:counter="item.quantity" />
                                <CounterIncrement @increment="increment(item)" />
                            </Counter>
                        </TableCell>
                        <TableCell>
                            <span class="font-sans text-base font-medium text-slate-800 tabular-nums"> {{ calculateTotalPrice(item) }} </span>
                        </TableCell>
                        <TableCell>
                            <RemoveCartItem :ticketId="item.ticket_id" />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Card>
        <CouponSection />
        <Button
            variant="outline"
            class="text-primary border-primary hover:bg-primary hover:text-primary-foreground h-[48px] w-8/12 text-sm md:w-5/12"
            :as="Link"
            href="/#tickets"
        >
            {{ $t('Keep shopping') }}
        </Button>
    </div>
</template>
